import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AuthService } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import { useSuperAdminAuthStore } from '@/store/superAdminAuthStore';
import { QUERY_KEYS, STORAGE_KEYS } from '@/constants';
import type { LoginResult } from '@/types/api';
import { getErrorMessage } from '@/utils/helpers';
import { clearBiometricCredentials } from '@/utils/biometric';

function applyLoginResult(result: LoginResult, rememberMe?: boolean) {
  useAuthStore.getState().setSession({
    user: result.user,
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    tenant: result.tenant,
    permissions: result.permissions,
    rememberMe,
  });
}

export function useAuthMe(enabled = true) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setUser = useAuthStore((s) => s.setUser);

  return useQuery({
    queryKey: QUERY_KEYS.authMe,
    queryFn: async () => {
      const res = await AuthService.me();
      setUser(res.data);
      if (res.data.permissions) {
        useAuthStore.setState({ permissions: res.data.permissions });
      }
      return res.data;
    },
    enabled: enabled && Boolean(accessToken),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useLoginEmail() {
  return useMutation({
    mutationFn: AuthService.loginWithEmail,
    onSuccess: (data, variables) => {
      applyLoginResult(data.data, variables.rememberMe);
    },
  });
}

export function useLoginMobile() {
  return useMutation({
    mutationFn: AuthService.loginWithMobile,
    onSuccess: (data, variables) => {
      applyLoginResult(data.data, variables.rememberMe);
    },
  });
}

export function useRequestOtp() {
  return useMutation({
    mutationFn: AuthService.requestOtp,
  });
}

export function useVerifyOtp() {
  return useMutation({
    mutationFn: AuthService.verifyOtp,
    onSuccess: (data, variables) => {
      applyLoginResult(data.data, variables.rememberMe);
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: AuthService.forgotPassword,
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: AuthService.resetPassword,
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: AuthService.changePassword,
    onSuccess: async () => {
      await clearBiometricCredentials().catch(() => {});
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = useAuthStore.getState().refreshToken;
      try {
        await AuthService.logout(refreshToken);
      } catch (error) {
        console.warn(getErrorMessage(error, 'Logout request failed'));
      } finally {
        await clearBiometricCredentials().catch(() => {});
        useAuthStore.getState().logout();
        useSuperAdminAuthStore.getState().clearPlatformAuth();
        useSuperAdminAuthStore.getState().clearImpersonation();
        queryClient.clear();
        try {
          localStorage.removeItem(STORAGE_KEYS.auth);
          localStorage.removeItem('sm.superadmin.auth');
          localStorage.removeItem('sm.token');
          localStorage.removeItem('sm.refresh_token');
          sessionStorage.clear();
        } catch {
          // ignore storage clearing errors
        }
      }
    },
  });
}
